import React from 'react';
import styled from 'styled-components';
import { FaRegBell } from 'react-icons/fa6';
import { useAuth } from '../contexts/AuthContext';

export default function NotificationsBell() {
  const { user, notifications = [], notifSeenAt = 0, markNotificationsSeen, followedMap = {} } = useAuth();
  const [open, setOpen] = React.useState(false);
  const [openedBatch, setOpenedBatch] = React.useState(null); // snapshot of items shown on open
  const [showAll, setShowAll] = React.useState(true); // default: show all recent (3 weeks)

  if (!user) return null;

  const unread = notifications.filter(n => (Number(n?.createdAt) || 0) > (notifSeenAt || 0));
  const recent = notifications; // server already prunes to 3 weeks
  const followedOnly = React.useMemo(() => recent.filter(n => n && n.popupId && followedMap[String(n.popupId)]), [recent, followedMap]);
  const unreadCount = unread.length;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
  // snapshot current view (all vs followed) to keep stable while panel is open
  const snapshot = (showAll ? recent : followedOnly);
      setOpenedBatch(snapshot);
      // mark as seen so badge clears, but keep showing the snapshot
      try { markNotificationsSeen && markNotificationsSeen(); } catch {}
    } else {
      // clear batch on close to recalc next time
      setOpenedBatch(null);
    }
  };

  const navigateFromNotification = (n) => {
    try {
      const target = n?.url ? String(n.url) : '';
      if (!target) return;
      // external links open in new tab
      if (/^https?:\/\//i.test(target)) {
        window.open(target, '_blank', 'noopener');
        setOpen(false);
        return;
      }
      // internal navigation
      const url = new URL(target, window.location.origin);
      const sameOrigin = url.origin === window.location.origin;
      if (!sameOrigin) {
        window.open(url.href, '_blank', 'noopener');
        setOpen(false);
        return;
      }
      // preview overlay support
      if (url.searchParams.has('preview')) {
        const id = url.searchParams.get('preview');
        // If we're navigating to a different pathname, force a full navigation and
        // stash the id so PreviewOnLoad can open the overlay after the load.
        if (url.pathname !== window.location.pathname) {
          try { sessionStorage.setItem('openPreviewId', String(id)); } catch {}
          // navigate so App.PreviewOnLoad picks up the preview id and opens overlay
          window.location.assign(url.pathname + url.search + url.hash);
        } else {
          // same page: update history and dispatch open directly
          window.history.pushState({}, '', url.pathname + url.search + url.hash);
          try { window.dispatchEvent(new CustomEvent('preview:open', { detail: { id } })); } catch {}
        }
      } else {
        window.history.pushState({}, '', url.pathname + url.search + url.hash);
        // let React Router handle route change naturally
      }
      setOpen(false);
    } catch {}
  };

  return (
    <Wrap>
      <BellButton onClick={toggle} aria-label={`Notifikace (${unreadCount})`}>
        <FaRegBell size={18} />
        {unreadCount > 0 && <Badge>{unreadCount}</Badge>}
      </BellButton>
      {open && (
        <Panel role="dialog" aria-modal="false" onClick={(e) => e.stopPropagation()}>
          <Tabs>
            <Tab aria-pressed={showAll} $active={showAll} onClick={() => { setShowAll(true); setOpenedBatch(recent); }}>Vše</Tab>
            <Tab aria-pressed={!showAll} $active={!showAll} onClick={() => { setShowAll(false); setOpenedBatch(followedOnly); }}>Sledované</Tab>
          </Tabs>
          {(openedBatch ? openedBatch : (showAll ? recent : followedOnly)).length === 0 ? (
            <Empty>Žádné notifikace</Empty>
          ) : (
            (openedBatch ? openedBatch : (showAll ? recent : followedOnly)).slice(0, 20).map(n => {
              return (
              <Item key={n.id} onClick={() => navigateFromNotification(n)}>
                {n.popupThumb ? (
                  <Thumb src={n.popupThumb} alt="thumb" />
                ) : (
                  <Placeholder />
                )}
                <Text>
                  <strong>{n.popupTitle || 'Oznámení'}</strong>
                  <span>{n.text || ''}</span>
                </Text>
              </Item>
              );
            })
          )}
        </Panel>
      )}
    </Wrap>
  );
}

const Wrap = styled.div`
  position: relative;
  margin-right: 10px;
`;

const BellButton = styled.button`
  position: relative;
  appearance: none;
  border: 0;
  background: transparent;
  color: #e6eef8;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
`;

const Badge = styled.span`
  position: absolute;
  top: -4px;
  right: -2px;
  background: #e53935;
  color: #fff;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  font-size: 11px;
  line-height: 18px;
  text-align: center;
  padding: 0 4px;
  font-weight: 700;
`;

const Panel = styled.div`
  position: absolute;
  top: 120%;
  right: 0;
  background: linear-gradient(180deg,#0f1720,#11131a);
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.06);
  width: 340px;
  max-height: 60vh;
  overflow: auto;
  box-shadow: 0 18px 40px rgba(2,6,23,0.55);
  z-index: 1001;
  padding: 8px;
`;

const Tabs = styled.div`
  display: flex;
  gap: 6px;
  margin: 4px 4px 8px 4px;
`;

const Tab = styled.button`
  appearance: none;
  border: 1px solid rgba(255,255,255,0.12);
  background: ${(p) => (p.$active ? 'rgba(255,255,255,0.08)' : 'transparent')};
  color: #e6eef8;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  cursor: pointer;
`;

const Empty = styled.div`
  padding: 16px;
  color: #cfdff6;
  text-align: center;
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  border-radius: 8px;
  &:hover { background: rgba(255,255,255,0.04); }
`;

const Thumb = styled.img`
  width: 72px;
  height: 40px;
  object-fit: cover;
  border-radius: 6px;
  flex: 0 0 auto;
`;

const Placeholder = styled.div`
  width: 72px;
  height: 40px;
  border-radius: 6px;
  background: rgba(255,255,255,0.06);
  flex: 0 0 auto;
`;

const Text = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  color: #e6eef8;
  strong { font-size: 13px; }
  span { font-size: 12px; opacity: 0.9; }
`;

// removed unread dot per new UX
